import { useEffect, useMemo, useState } from "react";
import { getCallerIdOptions, type CallerIdOption, type CallerIdOptions } from "@/services/communications.service";
import { formatUsPhone } from "@/lib/phone";
import { groupByFacility } from "./groupByFacility";

/**
 * The call panel's caller ID: which of the practice's reply lines the patient
 * sees, and can call back, when staff call them.
 *
 * Loads when the panel opens for a patient and starts on the number a call
 * with no choice would show (the patient's facility line, else the practice's
 * line). Moves only when staff pick another number.
 *
 * `callerNumber` is what to send with the call: the selected number, the
 * default included, so whichever server answers the call dials what the panel
 * showed. Undefined while nothing is loaded, when loading failed, or when the
 * practice has no listed numbers; the server then picks as it always has.
 *
 * `loading` is true until the request for this patient has settled, either
 * way. The panel holds Call until then, because a call placed mid-load sends
 * no number and would show the practice line instead of the default the panel
 * is about to display. A failed load ends `loading`, so the call still goes
 * out on the server's choice.
 */
export function useCallerIdChoice(patientId: string | null | undefined) {
  const [data, setData] = useState<CallerIdOptions | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // The patient whose request has settled. Compared with the current one
  // rather than kept as a flag, so the render right after a patient switch,
  // before the effect below has run, already counts as loading.
  const [settledFor, setSettledFor] = useState<string | null>(null);

  const wanted = patientId && Number(patientId) ? patientId : null;

  useEffect(() => {
    setData(null);
    setSelected(null);
    setLoadFailed(false);
    setSettledFor(null);
    if (!wanted) return;

    let cancelled = false;
    // Inside the chain, so a loader that throws before returning a promise is
    // handled like any other failure instead of breaking the panel.
    Promise.resolve()
      .then(() => getCallerIdOptions(Number(wanted)))
      .then((loaded) => {
        if (cancelled) return;
        const listed = new Set(loaded.options.map((o) => o.number));
        setData(loaded);
        setSelected(
          loaded.defaultNumber && listed.has(loaded.defaultNumber)
            ? loaded.defaultNumber
            : (loaded.options[0]?.number ?? null),
        );
        setSettledFor(wanted);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
        setSettledFor(wanted);
      });
    return () => {
      cancelled = true;
    };
  }, [wanted]);

  const options = data?.options ?? [];

  return {
    options,
    selected,
    setSelected,
    loadFailed,
    loading: wanted !== null && settledFor !== wanted,
    callerNumber: selected ?? undefined,
  };
}

function optionText(option: CallerIdOption): string {
  return `${formatUsPhone(option.number) || option.number} · ${option.label}`;
}

/**
 * Two or more numbers: a choice. One: the number, as text. None: nothing.
 * A list that could not be loaded says so; the call still goes out.
 */
export function CallerIdPicker({
  options,
  value,
  onChange,
  loadFailed,
}: {
  options: CallerIdOption[];
  value: string | null;
  onChange: (number: string) => void;
  loadFailed: boolean;
}) {
  const groups = useMemo(() => groupByFacility(options), [options]);

  if (loadFailed) {
    return (
      <p className="text-xs text-muted-foreground text-center">
        We couldn't load your practice's phone numbers, so this call will show the usual number.
      </p>
    );
  }

  if (options.length === 1) {
    return (
      <p className="text-xs text-muted-foreground text-center">
        Calling from {optionText(options[0])}
      </p>
    );
  }

  if (options.length === 0) return null;

  return (
    <label className="flex w-full max-w-[320px] flex-col gap-1">
      <span className="text-xs text-muted-foreground">Call from</span>
      <select
        className="h-9 w-full text-sm rounded-md border border-border bg-white px-2"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Call from"
      >
        {groups.map((group, index) => (
          <optgroup key={`${index}-${group.name}`} label={group.name}>
            {group.options.map((o) => (
              <option key={o.number} value={o.number}>
                {optionText(o)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}
